import { ActionIcon, Group } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import { i18nKeys, useTranslation } from 'locales';
import { useHistory } from 'react-router';
import { isNullish } from 'remeda';
import AddressBlock from 'shared/components/AddressBlock';
import { Icon, IconColor, IconName } from 'shared/components/Icon';
import { DialogShowId, dialogHide } from 'store/view/view.actions';
import type { Debtor } from 'types/store/client-state';
import type { Invoice } from 'types/store/invoice-state';
import styleIdentifiers from './debtorInfo.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  imported?: boolean;
  invoiceData?: Invoice;
  clientData: Debtor;
  label?: string;
};

export default function DebtorInfo({ imported, invoiceData, clientData }: Props) {
  const { t } = useTranslation();
  const history = useHistory();

  const seeClient = () => {
    dialogHide(DialogShowId.INVOICE);
    history.push(`/clients/${clientData.id}`);
  };

  if (isNullish(clientData)) return null;

  return (
    <Panel
      withBorder
      title={clientData.full_name}
      className={styles('box', 'client-info', 'className')}
      topRight={
        <Group gap="xs">
          {clientData.paused && (
            <Icon
              name={IconName.PAUSE}
              color={IconColor.DARK}
              size="18px"
              title={t(i18nKeys.RECOVERY_IN_PAUSE)}
            />
          )}
          {!imported && (
            <ActionIcon variant="subtle" color="gray" onClick={seeClient}>
              <IconEye stroke={1.5} size={28} />
            </ActionIcon>
          )}
        </Group>
      }
    >
      <div className={styles('item', 'block')}>
        <span className={styles('subtitle')}>{t(i18nKeys.ADDRESS)}</span>
        <div className={styles('value', 'medium')}>
          <AddressBlock address={clientData.address_attributes} className={styles('address')} />
        </div>
      </div>
      {clientData.phones_attributes.length > 0 && (
        <div className={styles('item', 'block')}>
          <span className={styles('subtitle')}>{t(i18nKeys.PHONE)}</span>
          <a
            href={`tel:${
              clientData.phones_attributes[clientData.phones_attributes.length - 1].number
            }`}
            className={styles('value', 'medium')}
          >
            {clientData.phones_attributes[clientData.phones_attributes.length - 1].number}
          </a>
        </div>
      )}
      <div className={styles('item', 'block')}>
        <span className={styles('subtitle')}>{t(i18nKeys.CONTACT)}</span>
        <span className={styles('value', 'medium')}>{clientData.full_name}</span>
      </div>
      <div className={styles('item', 'block')}>
        <span className={styles('subtitle')}>{t(i18nKeys.EMAIL)}</span>
        {(invoiceData?.email_missing ?? !clientData.emails[0]) ? (
          <div className={styles('error')}>{t(i18nKeys.MISSING_EMAIL)}</div>
        ) : (
          <a href={`mailto:${clientData.emails[0]}`} className={styles('value', 'medium')}>
            {clientData.emails[0]}
          </a>
        )}
      </div>
      {clientData.account_manager && (
        <div className={styles('item', 'block')}>
          <span className={styles('subtitle')}>{t(i18nKeys.ACCOUNT_MANAGER)}</span>
          <span className={styles('value', 'medium')}>{clientData.account_manager.email}</span>
        </div>
      )}
    </Panel>
  );
}
